// pages/inventory/SerialNoEntry.jsx
// ✅ Full conversion of SerialNoEntry.aspx — ALL types
// ✅ GRN, OB  → pnlOBGRN: text input rows (gvSno)
// ✅ STNO, SI, STNI, SCRI, SCRR → pnlSTN: checkbox select rows (gvSTN)
// ✅ window.opener.postMessage sends data back to parent tab
// ✅ All validation rules preserved from ASP
//
// ✅ FIX: parseQS() supports BOTH param naming styles:
//
//   ASP style (used by ALL existing pages):
//     pro=SCRR   qty=2   f=1
//
//   New style (also accepted):
//     type=SCRR  stockQty=2  flag=1
//
// ✅ URL PATTERNS (actual, from ASP source):
//   GRN  flag=1: ?item=ITEMCODE-STOREID-BRANCHID-ROWINDEX&qty=N&pro=GRN&f=1
//   GRN  flag=2: ?item=ITEMCODE-STOREID-BRANCHID-RECEIPTID-ROWINDEX&qty=N&pro=GRN&f=2
//   OB   flag=1: ?item=ITEMID&qty=N&pro=OB&f=1
//   STNO flag=1: ?item=ITEMID-STOREID-BRANCHID-ISSID&qty=N&pro=STNO&f=1
//   STNO flag=2: ?item=ITEMID-STOREID-BRANCHID&qty=N&pro=STNO&f=2&stnOutNo=X
//   SI   flag=1: ?item=ITEMID-STOREID-BRANCHID-DEPT-VEHICLE-DATE-ROWINDEX&qty=N&pro=SI&f=1
//   SI   flag=2: ?item=ITEMID-STOREID-BRANCHID&qty=N&pro=SI&f=2&issueNo=X
//   STNI flag=1: ?item=ITEMID-BRANCHID-STOREID-STNOUTWARDID&qty=N&pro=STNI&f=1
//   SCRI flag=1: ?item=ITEMID-BRANCHID-STOREID-SCRID&qty=N&pro=SCRI&f=1
//   SCRR flag=1: ?item=ITEMID-STOREID-BRANCHID-SCRIDETID&qty=N&pro=SCRR&f=1
//              NOTE: SCRR order is STOREID-BRANCHID (listing page sends store first)

import { useEffect, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Save, Hash, AlertCircle, ChevronDown, ChevronUp, Loader2
} from "lucide-react";

const BASE_URL = "http://nissi-india.com";
const API      = `${BASE_URL}/api/inventory/serial-no-entry`;

// ─── Panel classification ────────────────────────────────────────
const INPUT_PANEL_TYPES    = ["GRN", "OB"];
const CHECKBOX_PANEL_TYPES = ["STNO", "SI", "STNI", "SCRI", "SCRR"];

// ─── Helpers ────────────────────────────────────────────────────
const str    = (v) => (v ?? "").toString().trim();
const toInt  = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
const toBool = (v) => v === true || v === "True" || v === "true" || v === 1 || v === "1";

// ────────────────────────────────────────────────────────────────
//  parseQS — supports BOTH URL param naming styles
//
//  ASP pages send:  pro=SCRR  qty=2  f=1
//  New style sends: type=SCRR stockQty=2 flag=1
// ────────────────────────────────────────────────────────────────
function parseQS() {
  const sp   = new URLSearchParams(window.location.search);
  const item = str(sp.get("item"));

  // ── type ────────────────────────────────────────────────────
  // "pro" = ASP legacy   "type" = new style
  const type = str(sp.get("pro") || sp.get("type") || "").toUpperCase();

  // ── stockQty ────────────────────────────────────────────────
  // "qty" = ASP legacy   "stockQty" = new style
  const rawQty = sp.get("qty") !== null ? sp.get("qty") : sp.get("stockQty");
  const stockQty = toInt(rawQty, 0);

  // ── flag ─────────────────────────────────────────────────────
  // "f" = ASP legacy     "flag" = new style
  const rawFlag = sp.get("f") !== null ? sp.get("f") : sp.get("flag");
  const flag    = toInt(rawFlag, 1);

  const parts = item.split("-");

  let itemCode = "", itemId = 0, storeId = "", branchId = "",
      rowIndex = 0, receiptId = "", dept = "", vehicle = "",
      issueDate = "", stnOutwardId = "", stnOutNo = "",
      issueNo = "", scriDetId = "";

  if (type === "GRN") {
    // ITEMCODE-STOREID-BRANCHID-ROWINDEX        (flag 1)
    // ITEMCODE-STOREID-BRANCHID-RECEIPTID-ROWINDEX (flag 2)
    itemCode = str(parts[0]);
    storeId  = str(parts[1] ?? "");
    branchId = str(parts[2] ?? "");
    if (flag === 1) {
      rowIndex = toInt(parts[3]);
    } else {
      receiptId = str(parts[3] ?? "");
      rowIndex  = toInt(parts[4]);
    }

  } else if (type === "OB") {
    // ITEMID  (may also have STOREID-BRANCHID)
    itemId   = toInt(parts[0]);
    itemCode = str(parts[0]);
    storeId  = str(parts[1] ?? "");
    branchId = str(parts[2] ?? "");

  } else if (type === "STNO") {
    // ITEMID-STOREID-BRANCHID-ISSID  (flag 1)
    // ITEMID-STOREID-BRANCHID        (flag 2)
    itemId   = toInt(parts[0]);
    itemCode = str(parts[0]);
    storeId  = str(parts[1] ?? "");
    branchId = str(parts[2] ?? "");
    if (flag === 1) stnOutwardId = str(parts[3] ?? "");
    stnOutNo = str(sp.get("stnOutNo") ?? "");

  } else if (type === "SI") {
    // ITEMID-STOREID-BRANCHID-DEPT-VEHICLE-DATE-ROWINDEX  (flag 1)
    // ITEMID-STOREID-BRANCHID                             (flag 2)
    itemId    = toInt(parts[0]);
    itemCode  = str(parts[0]);
    storeId   = str(parts[1] ?? "");
    branchId  = str(parts[2] ?? "");
    dept      = str(parts[3] ?? "");
    vehicle   = str(parts[4] ?? "");
    issueDate = str(parts[5] ?? "");
    rowIndex  = toInt(parts[6]);
    issueNo   = str(sp.get("issueNo") ?? "");

  } else if (type === "STNI") {
    // ITEMID-BRANCHID-STOREID-STNOUTWARDID
    itemId       = toInt(parts[0]);
    itemCode     = str(parts[0]);
    branchId     = str(parts[1] ?? "");
    storeId      = str(parts[2] ?? "");
    stnOutwardId = str(parts[3] ?? "");

  } else if (type === "SCRI") {
    // ASP Entry page: item=ITEMID-BRANCHID-STOREID-DATE
    // ?item=751-1-8-2025/07/30&qty=2&pro=SCRI&f=1
    itemId    = toInt(parts[0]);
    itemCode  = str(parts[0]);
    branchId  = str(parts[1] ?? "");
    storeId   = str(parts[2] ?? "");
    scriDetId = str(parts[3] ?? ""); // date string from Entry page

  } else if (type === "SCRR") {
    // ASP Listing page: item=ITEMID-STOREID-BRANCHID-SCRIDETID
    // ?item=751-1-8-1087&qty=2&pro=SCRR&f=1
    // NOTE: order is STOREID then BRANCHID (opposite of SCRI)
    itemId    = toInt(parts[0]);
    itemCode  = str(parts[0]);
    storeId   = str(parts[1] ?? "");   // ← position [1] = store
    branchId  = str(parts[2] ?? "");   // ← position [2] = branch
    scriDetId = str(parts[3] ?? "");   // SCRIssueDetid

  } else {
    // Unknown / future type — best-effort extraction
    itemCode = str(parts[0]);
    itemId   = toInt(parts[0]);
    branchId = str(parts[1] ?? "");
    storeId  = str(parts[2] ?? "");
    scriDetId= str(parts[3] ?? "");
  }

  // Extra params from parent window or localStorage
  const finYearId   = str(sp.get("finYearId")   ?? localStorage.getItem("finYearId") ?? "");
  const loginUserId = str(sp.get("loginUserId") ?? localStorage.getItem("userId")    ?? "");
  const entryDate   = str(sp.get("entryDate")   ?? "");
  const receiptCode = str(sp.get("receiptCode") ?? "");

  return {
    item, itemCode, itemId, storeId, branchId, rowIndex, receiptId,
    dept, vehicle, issueDate, stnOutwardId, stnOutNo, issueNo, scriDetId,
    stockQty, type, flag, finYearId, loginUserId, entryDate, receiptCode
  };
}

// ════════════════════════════════════════════════════════════════
//  Main Component
// ════════════════════════════════════════════════════════════════
export default function SerialNoEntry() {
  const qs = parseQS();
  const {
    itemCode, storeId, branchId, rowIndex, receiptId,
    dept, vehicle, issueDate, stnOutwardId, stnOutNo,
    issueNo, scriDetId, stockQty, type, flag, finYearId,
    loginUserId, entryDate, receiptCode
  } = qs;
  let { itemId } = qs;

  const isDark          = window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  const isInputPanel    = INPUT_PANEL_TYPES.includes(type);
  const isCheckboxPanel = CHECKBOX_PANEL_TYPES.includes(type);

  // ── State ───────────────────────────────────────────────────────
  const [itemLabel,      setItemLabel]      = useState("Loading...");
  const [resolvedItemId, setResolvedItemId] = useState(itemId);
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [errorMsg,       setErrorMsg]       = useState("");

  // pnlOBGRN — text input rows
  const [snoInputs,    setSnoInputs]    = useState([]);
  const [existingRows, setExistingRows] = useState([]);
  const [waitingRows,  setWaitingRows]  = useState([]);

  // pnlSTN — checkbox rows
  const [stnRows,          setStnRows]          = useState([]);
  const [selectedRows,     setSelectedRows]     = useState([]);
  const [existingSTN,      setExistingSTN]      = useState([]);
  const [scrrReceived,     setScrrReceived]     = useState([]);
  const [isDisabled,       setIsDisabled]       = useState(false);
  const [showPrice,        setShowPrice]        = useState(false);
  const [deptVehicleLabel, setDeptVehicleLabel] = useState("");

  // Accordion
  const [showExisting, setShowExisting] = useState(true);
  const [showWaiting,  setShowWaiting]  = useState(true);

  // ── Styles ──────────────────────────────────────────────────────
  const bg      = isDark ? "bg-gray-900 text-white"      : "bg-gray-50 text-gray-900";
  const card    = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const inputCl = isDark
    ? "bg-gray-700 border-gray-600 text-white focus:ring-blue-500"
    : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500";
  const thCl    = "px-4 py-3 text-xs font-bold uppercase tracking-wide text-white text-left whitespace-nowrap";
  const tdCl    = `px-4 py-2.5 text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`;
  const stripCl = (i) => i % 2 === 0
    ? (isDark ? "bg-white/5"      : "bg-gray-50")
    : (isDark ? "bg-transparent" : "bg-white");

  // ════════════════════════════════════════════════════════════════
  //  INIT
  // ════════════════════════════════════════════════════════════════
  useEffect(() => { initPage(); /* eslint-disable-next-line */ }, []);

  const initPage = async () => {
    try {
      setLoading(true);

      // Guard: unknown type
      if (!type) {
        setErrorMsg(
          `Missing entry type. URL must include pro=SCRR (ASP style) or type=SCRR (new style).\n` +
          `Received params: ${window.location.search}`
        );
        setLoading(false);
        return;
      }

      // Guard: missing item code/id
      const resolvedCode = str(itemCode || itemId);
      if (!resolvedCode) {
        setErrorMsg("Item code/id is missing from the URL.");
        setLoading(false);
        return;
      }

      // opId=3 → GRN (itemCode is a string code)
      // opId=2 → OB edit (flag=2)
      // opId=1 → all others (itemCode is numeric itemId)
      const opId = type === "GRN" ? 3 : (type === "OB" && flag === 2 ? 2 : 1);

      const infoRes = await axios.get(`${API}/item-info`, {
        params: {
          itemCode:  resolvedCode,
          opId,
          itemId:    itemId || 0,
          branchId,
          storeId,
          finYearId
        }
      });

      if (infoRes.data?.success) {
        setItemLabel(infoRes.data.itemLabel || `Item: ${resolvedCode}`);
        const rid = toInt(infoRes.data.itemId) || itemId;
        setResolvedItemId(rid);
        itemId = rid;

        if (isInputPanel)    await loadInputPanel(rid);
        else                 await loadCheckboxPanel(rid);
      } else {
        setErrorMsg(infoRes.data?.message || "Failed to load item info.");
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Unknown error";
      setErrorMsg("Init failed: " + msg);
      console.error("SerialNoEntry init error:", e);
    } finally {
      setLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════════════
  //  pnlOBGRN — INPUT PANEL (GRN, OB)
  // ════════════════════════════════════════════════════════════════
  const loadInputPanel = async (rid) => {
    if (type === "GRN" && flag === 2) {
      try {
        const r = await axios.get(`${API}/grn-edit-details`, {
          params: { branchId, itemId: rid, storeId, receiptId, rowIndex, finYearId }
        });
        if (r.data?.success && r.data.rows?.length > 0) {
          buildInputRows(r.data.rows, stockQty);
        } else {
          buildEmptyInputRows(stockQty);
        }
      } catch { buildEmptyInputRows(stockQty); }
    } else {
      buildEmptyInputRows(stockQty);
    }

    try {
      const [exRes, waitRes] = await Promise.all([
        axios.get(`${API}/existing-serial-nos`,  { params: { itemId: rid, branchId, storeId, finYearId } }),
        axios.get(`${API}/waiting-serial-nos`,   { params: { itemId: rid, branchId, storeId, finYearId } }),
      ]);
      if (exRes.data?.success)   setExistingRows(exRes.data.rows   || []);
      if (waitRes.data?.success) setWaitingRows(waitRes.data.rows  || []);
    } catch { /* non-fatal */ }
  };

  const buildEmptyInputRows = (qty) => {
    setSnoInputs(Array.from({ length: qty }, (_, i) => ({
      rowNumber: i + 1, itemSnoId: "", value: "", error: false,
    })));
  };

  const buildInputRows = (dbRows, qty) => {
    const rows = dbRows.map((r, i) => ({
      rowNumber: toInt(r.RowNumber, i + 1),
      itemSnoId: str(r.ItemSnoId ?? ""),
      value:     str(r.ItemSno   ?? ""),
      error:     false,
    }));
    while (rows.length < qty) rows.push({ rowNumber: rows.length + 1, itemSnoId: "", value: "", error: false });
    setSnoInputs(rows.slice(0, qty));
  };

  // ════════════════════════════════════════════════════════════════
  //  pnlSTN — CHECKBOX PANEL (STNO, SI, STNI, SCRI, SCRR)
  // ════════════════════════════════════════════════════════════════
  const loadCheckboxPanel = async (rid) => {
    let rows     = [];
    let disabled = false;
    let priceCol = false;

    try {
      if (type === "STNO") {
        if (flag === 1) {
          const r = await axios.get(`${API}/stno-new-details`, {
            params: { branchId, itemId: rid, storeId, issId: stnOutwardId, finYearId }
          });
          rows = r.data?.rows || [];
        } else {
          const r = await axios.get(`${API}/stno-edit-details`, {
            params: { branchId, itemId: rid, storeId, stnOutNo, finYearId }
          });
          rows = r.data?.rows || [];
        }

      } else if (type === "SI") {
        setDeptVehicleLabel(dept && vehicle ? `${dept} :: ${vehicle}` : "");
        if (flag === 1) {
          const r = await axios.get(`${API}/si-new-details`, {
            params: { branchId, itemId: rid, storeId, issueDate, finYearId }
          });
          rows = r.data?.rows || [];
        } else {
          const r = await axios.get(`${API}/si-edit-details`, {
            params: { branchId, itemId: rid, storeId, storesIssueNo: issueNo, finYearId }
          });
          rows = r.data?.rows || [];
        }

      } else if (type === "STNI") {
        const r = await axios.get(`${API}/stni-new-details`, {
          params: { stnOutwardId, itemId: str(rid), branchId, storeId, flag, finYearId }
        });
        rows     = r.data?.rows     || [];
        disabled = r.data?.isDisabled ?? false;

      } else if (type === "SCRI") {
        // scriDetId = date string from Entry page (item=ITEMID-BRANCHID-STOREID-DATE)
        const r = await axios.get(`${API}/scri-new-details`, {
          params: { scrId: scriDetId, itemId: str(rid), branchId, storeId, flag, finYearId }
        });
        rows     = r.data?.rows           || [];
        priceCol = r.data?.showPriceColumn ?? false;
        disabled = r.data?.isDisabled      ?? false;

      } else if (type === "SCRR") {
        // scriDetId = SCRIssueDetid from Listing page (item=ITEMID-STOREID-BRANCHID-SCRIDETID)
        const [newRes, recvRes] = await Promise.all([
          axios.get(`${API}/scrr-new-details`, {
            params: { scrId: scriDetId, itemId: str(rid), branchId, storeId, finYearId }
          }),
          axios.get(`${API}/scrr-received`, {
            params: { scriDetId, itemId: str(rid), branchId, storeId, finYearId }
          }),
        ]);
        rows = newRes.data?.rows || [];
        setScrrReceived(recvRes.data?.rows || []);
      }
    } catch (e) {
      console.error("loadCheckboxPanel error:", e.message);
      setErrorMsg("Failed to load serial nos: " + (e?.response?.data?.message || e.message));
    }

    setStnRows(rows.map(r => ({
      ...r,
      IsSelected: toBool(r.IsSelected),
      // Normalize price — SP may return: price, Price, fld_price, fld_Price, tyre_price
      Price: (() => {
        const raw = r.price ?? r.Price ?? r.fld_price ?? r.fld_Price ?? r.tyre_price ?? r.TyrePrice ?? "";
        const n   = parseFloat(raw);
        return Number.isFinite(n) ? String(n) : "0";
      })(),
      // Normalize ItemSnoId — SP may return: ItemSnoId, fld_itemsnoid, fld_tyreid, tyreid, ItemSno_Id
      ItemSnoId: str(
        r.ItemSnoId   ?? r.itemSnoId   ?? r.ItemSnoid   ??
        r.fld_itemsnoid ?? r.fld_ItemSnoId ??
        r.fld_tyreid  ?? r.tyreid      ?? r.TyreId      ??
        r.fld_id      ?? ""
      ),
    })));
    setIsDisabled(disabled);
    setShowPrice(priceCol);

    // Existing STN serial nos (right panel)
    try {
      const exRes = await axios.get(`${API}/existing-stn-serial-nos`, {
        params: { itemId: rid, branchId, storeId, finYearId }
      });
      if (exRes.data?.success) setExistingSTN(exRes.data.rows || []);
    } catch { setExistingSTN([]); }
  };

  // ════════════════════════════════════════════════════════════════
  //  VALIDATION — INPUT PANEL
  // ════════════════════════════════════════════════════════════════
  const validateInputRows = async (inputs) => {
    setErrorMsg("");
    setSnoInputs(prev => prev.map(r => ({ ...r, error: false })));

    for (let j = 0; j < inputs.length; j++) {
      const sno = str(inputs[j].value);

      if (!sno) {
        setSnoInputs(prev => prev.map((r, i) => i === j ? { ...r, error: true } : r));
        setErrorMsg("Please Fill The Details.");
        return false;
      }

      if (inputs.filter(r => str(r.value) === sno).length > 1) {
        setSnoInputs(prev => prev.map((r, i) => i === j ? { ...r, error: true } : r));
        setErrorMsg(`${sno} serial no exists more than once. pl correct`);
        return false;
      }

      try {
        const r = await axios.get(`${API}/check-duplicate`, {
          params: { serialNo: sno, itemId: resolvedItemId, branchId, storeId, finYearId }
        });
        if (r.data?.isDuplicate) {
          setSnoInputs(prev => prev.map((r2, i) =>
            i === j ? { ...r2, value: "", error: true } : r2));
          setErrorMsg(r.data.sameBranch
            ? `${sno} serial no already exists. Please change the Serial No`
            : `${sno} serial no already exists in another branch. Please change the Serial No`);
          return false;
        }
      } catch (e) {
        setErrorMsg("Validation error: " + e.message);
        return false;
      }

      if (type === "GRN") {
        for (const w of waitingRows) {
          const wSno   = str(w.ItemSno);
          const wMasId = str(w.masid ?? w.MasID ?? "");
          const wRow   = toInt(w.fld_RowIndex ?? w.RowIndex);
          if (wSno === sno) {
            const masIdStr = str(receiptId);
            if (wMasId !== masIdStr || (wMasId === masIdStr && wRow !== rowIndex)) {
              setSnoInputs(prev => prev.map((r, i) => i === j ? { ...r, error: true } : r));
              setErrorMsg(`${sno} serial no Already exist for another Inspection. pl enter other serial no`);
              return false;
            }
          }
        }
      }
    }
    return true;
  };

  // ════════════════════════════════════════════════════════════════
  //  VALIDATION — CHECKBOX PANEL
  // ════════════════════════════════════════════════════════════════
  const validateCheckboxRows = (checked) => {
    setErrorMsg("");
    if (checked.length === 0 && stockQty > 0) { setErrorMsg("Please Select Any One"); return false; }
    if (type === "SI"   && flag === 1 && checked.length !== stockQty) { setErrorMsg("Issue Qty & Serial No.Qty are miss match"); return false; }
    if (type === "STNI" && checked.length !== stockQty)               { setErrorMsg("No of Serial No's should match the Rejected qty"); return false; }
    if (type === "SCRI" && checked.length !== stockQty)               { setErrorMsg("No of Serial No's should match the Rejected qty"); return false; }
    if (type === "STNO" && checked.length !== stockQty)               { setErrorMsg("Qty miss matches. Pl check"); return false; }
    return true;
  };

  // ════════════════════════════════════════════════════════════════
  //  SAVE — INPUT PANEL (GRN, OB)
  // ════════════════════════════════════════════════════════════════
  const handleInputSave = async () => {
    if (saving) return;
    const isValid = await validateInputRows(snoInputs);
    if (!isValid) return;
    setSaving(true);
    try {
      const serialNos = snoInputs.map(r => ({
        RowNumber: r.rowNumber,
        ItemSno:   str(r.value),
        ItemSnoId: r.itemSnoId || 0,
      }));

      const res = await axios.post(`${API}/save-serial-nos`, {
        type, flag,
        itemId:      resolvedItemId,
        storeId:     toInt(storeId),
        branchId:    toInt(branchId),
        receiptId:   toInt(receiptId) || 0,
        rowIndex,
        loginUserId: toInt(loginUserId),
        entryDate, receiptCode,
        finYearId:   toInt(finYearId),
        serialNos,
      });

      if (!res.data?.success) { setErrorMsg(res.data?.message || "Save failed."); return; }

      sendToParent({
        type:      `${type}_SERIAL_NOS`,
        entryType: type, flag,
        itemId:    resolvedItemId, itemCode, storeId, branchId,
        rowIndex,  receiptId: receiptId || "",
        stockQty,  serialNos: res.data.savedSerialNos || serialNos,
      });
    } catch (e) {
      setErrorMsg("Save failed: " + (e?.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  // ════════════════════════════════════════════════════════════════
  //  SAVE — CHECKBOX PANEL (STNO, SI, STNI, SCRI, SCRR)
  //  For SCRI/SCRR: also writes to sessionStorage for parent page
  // ════════════════════════════════════════════════════════════════
  const handleCheckboxSave = () => {
    if (saving) return;
    const checked  = stnRows.filter(r => r.IsSelected);
    const rejected = stnRows.filter(r => !r.IsSelected);
    if (!validateCheckboxRows(checked)) return;
    setSaving(true);
    try {
      const serialNos = checked.map(r => ({
        RowNumber:  str(r.RowNumber),
        itemid:     str(r.itemid ?? resolvedItemId),
        ItemSno:    str(r.ItemSno),
        // Send both casings so Node can find it regardless
        ItemSnoId:  str(r.ItemSnoId ?? ""),
        IsSelected: true, IsRejected: false,
        Dept:       dept, Vehicle: vehicle,
        // Always send lowercase 'price' key — Node reads sno.price
        price:      (() => { const n = parseFloat(r.Price ?? "0"); return Number.isFinite(n) ? n : 0; })(),
        Price:      (() => { const n = parseFloat(r.Price ?? "0"); return Number.isFinite(n) ? n : 0; })(),
        RowIndex:   rowIndex,
      }));
      const rejectedNos = rejected.map(r => ({
        RowNumber:  str(r.RowNumber),
        itemid:     str(r.itemid ?? resolvedItemId),
        ItemSno:    str(r.ItemSno),
        ItemSnoId:  str(r.ItemSnoId ?? ""),
        IsSelected: false, IsRejected: true,
        Dept:       dept, Vehicle: vehicle,
        Price:      str(r.Price ?? "0"),
        RowIndex:   rowIndex,
      }));

      // ── Write to sessionStorage for SCRI / SCRR parent pages ──
      // Parent reads: sessionStorage["SCRBranchItemSno_{userId}_{itemId}"]  (SCRI)
      //               sessionStorage["SCRRBranchItemSno_{userId}_{itemId}"] (SCRR)
      if (type === "SCRI" || type === "SCRR") {
        const prefix = type === "SCRI" ? "SCRBranchItemSno" : "SCRRBranchItemSno";
        const uid    = loginUserId || localStorage.getItem("userId") || "";
        const key    = `${prefix}_${uid}_${resolvedItemId}`;
        const snoForSession = serialNos.map(s => ({
          ItemSno:   s.ItemSno,
          ItemSnoId: s.ItemSnoId,   // tyreid — parent reads as [0] and [1] split
          itemid:    resolvedItemId,
          price:     s.Price || "0"
        }));
        sessionStorage.setItem(key, JSON.stringify(snoForSession));
      }

      sendToParent({
        type:      `${type}_SERIAL_NOS`,
        entryType: type, flag,
        itemId:    resolvedItemId, itemCode, storeId, branchId,
        rowIndex,  dept, vehicle, stockQty,
        serialNos, rejectedNos, stnOutwardId, issueNo, scriDetId,
      });
    } finally {
      setSaving(false);
    }
  };

  // ─── postMessage → parent, fallback → sessionStorage ─────────────
  const sendToParent = (message) => {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(message, "*");
      toast.success("Saved! Closing...", { autoClose: 700 });
      setTimeout(() => window.close(), 900);
    } else {
      const key = `SNO_${type}_${resolvedItemId}_${rowIndex}`;
      sessionStorage.setItem(key, JSON.stringify(message));
      toast.success("Saved! You may close this tab.", { autoClose: 2000 });
      setTimeout(() => window.close(), 1500);
    }
  };

  // ─── Checkbox helpers ─────────────────────────────────────────────
  const handleSelectAll  = (c)    => { if (!isDisabled) setStnRows(p => p.map(r => ({ ...r, IsSelected: c }))); };
  const toggleRow        = (idx)  => { if (!isDisabled) setStnRows(p => p.map((r, i) => i === idx ? { ...r, IsSelected: !r.IsSelected } : r)); };
  const handlePriceChange= (i, v) => setStnRows(p => p.map((r, idx) => idx === i ? { ...r, Price: v } : r));

  // ─── Input helpers ────────────────────────────────────────────────
  const handleInputChange = (idx, val) => {
    setSnoInputs(p => p.map((r, i) => i === idx ? { ...r, value: val.toUpperCase(), error: false } : r));
    if (errorMsg) setErrorMsg("");
  };
  const handleKeyDown = (e, idx) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const next = document.getElementById(`sno-${idx + 1}`);
      if (next) next.focus(); else handleInputSave();
    }
  };

  // ─── Badge helpers ────────────────────────────────────────────────
  const typeBadgeColor = ({
    GRN: "bg-blue-100 text-blue-700", OB:   "bg-purple-100 text-purple-700",
    STNO:"bg-orange-100 text-orange-700", SI: "bg-green-100 text-green-700",
    STNI:"bg-teal-100 text-teal-700",  SCRI:"bg-pink-100 text-pink-700",
    SCRR:"bg-red-100 text-red-700",
  })[type] || "bg-gray-100 text-gray-700";

  const flagLabel = ({ 1:"NEW", 2:"EDIT", 3:"VIEW" })[flag] || "NEW";

  // ════════════════════════════════════════════════════════════════
  //  LOADING STATE
  // ════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bg}`}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-sm font-medium">Loading Serial No Entry...</p>
          <p className="text-xs text-gray-400">{type || "(detecting type...)"} — Item: {itemCode || itemId}</p>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════════════
  return (
    <div className={`min-h-screen p-5 ${bg}`}>
      <ToastContainer position="top-right" autoClose={1000} theme={isDark ? "dark" : "light"} />

      {/* ── Header ──────────────────────────────────────────────── */}
      <div className={`rounded-2xl border p-5 mb-5 shadow ${card}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
            <Hash size={16} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-extrabold">Serial No Entry</h1>
            <p className={`text-xs font-medium mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {itemLabel}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <span className={`px-3 py-1 rounded-lg text-xs font-bold ${typeBadgeColor}`}>{type || "UNKNOWN"}</span>
            <span className={`px-3 py-1 rounded-lg text-xs font-bold ${
              flag === 1 ? "bg-green-100 text-green-700" :
              flag === 2 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"
            }`}>{flagLabel}</span>
          </div>
        </div>

        {/* Meta pills */}
        <div className="flex flex-wrap gap-2 mt-4">
          {[
            { label: "Item",    val: itemCode  },
            { label: "Store",   val: storeId   },
            { label: "Branch",  val: branchId  },
            { label: "Qty",     val: stockQty  },
            ...(rowIndex      ? [{ label: "Row",     val: rowIndex      }] : []),
            ...(receiptId     ? [{ label: "Receipt", val: receiptId     }] : []),
            ...(dept          ? [{ label: "Dept",    val: dept          }] : []),
            ...(vehicle       ? [{ label: "Vehicle", val: vehicle       }] : []),
            ...(stnOutwardId  ? [{ label: "STN Out", val: stnOutwardId  }] : []),
            ...(scriDetId     ? [{ label: "SCR ID",  val: scriDetId     }] : []),
          ].map(p => (
            <span key={p.label} className={`px-3 py-1 rounded-lg text-xs font-medium border ${
              isDark ? "border-white/10 bg-white/5 text-gray-300" : "border-gray-200 bg-gray-50 text-gray-600"
            }`}>
              {p.label}: <b>{p.val}</b>
            </span>
          ))}
        </div>

        {deptVehicleLabel && (
          <div className={`mt-3 px-4 py-2 rounded-xl text-sm font-semibold ${
            isDark ? "bg-green-900/20 text-green-300" : "bg-green-50 text-green-700"
          }`}>{deptVehicleLabel}</div>
        )}
      </div>

      {/* ── Error ────────────────────────────────────────────────── */}
      {errorMsg && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-semibold whitespace-pre-wrap">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          PANEL A — pnlOBGRN: text input rows (GRN, OB)
          ══════════════════════════════════════════════════════════ */}
      {isInputPanel && (
        <div className={`rounded-2xl border p-5 mb-5 shadow ${card}`}>
          <h2 className={`text-xs font-bold uppercase tracking-widest mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Enter Serial Nos — {stockQty} required
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue-600">
                <tr>
                  <th className={`${thCl} w-16`}>S.No</th>
                  <th className={thCl}>Item Serial No</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {snoInputs.map((row, idx) => (
                  <tr key={idx} className={stripCl(idx)}>
                    <td className={`${tdCl} text-center font-bold`}>{row.rowNumber}</td>
                    <td className={tdCl}>
                      <input
                        id={`sno-${idx}`}
                        type="text"
                        value={row.value}
                        maxLength={15}
                        autoComplete="off"
                        onChange={e => handleInputChange(idx, e.target.value)}
                        onKeyDown={e => handleKeyDown(e, idx)}
                        placeholder={`Serial No ${row.rowNumber}`}
                        className={`w-full max-w-xs px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 transition ${inputCl} ${
                          row.error ? "border-red-500 ring-1 ring-red-400" : ""
                        }`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-5 flex justify-center">
            <button onClick={handleInputSave} disabled={saving}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg transition-all text-sm disabled:opacity-60">
              {saving ? <><Loader2 size={15} className="animate-spin" /> Saving...</> : <><Save size={15} /> Save &amp; Close</>}
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          PANEL B — pnlSTN: checkbox rows (STNO, SI, STNI, SCRI, SCRR)
          ══════════════════════════════════════════════════════════ */}
      {isCheckboxPanel && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-5">

          {/* Left — available serial nos */}
          <div className={`rounded-2xl border shadow ${card}`}>
            <div className="px-5 pt-4 pb-2 flex items-center justify-between">
              <h2 className={`text-xs font-bold uppercase tracking-widest ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Available Serial Nos
                {stockQty > 0 && <span className="ml-2 text-blue-500">— Select {stockQty}</span>}
              </h2>
              {!isDisabled && (
                <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none">
                  <input type="checkbox" className="w-4 h-4 accent-blue-600 cursor-pointer"
                    onChange={e => handleSelectAll(e.target.checked)}
                    checked={stnRows.length > 0 && stnRows.every(r => r.IsSelected)}
                  />
                  Select All
                </label>
              )}
            </div>

            <div className="overflow-x-auto">
              {stnRows.length === 0 ? (
                <p className="text-center py-8 text-sm text-gray-400">No serial nos available</p>
              ) : (
                <table className="w-full">
                  <thead className="bg-blue-600">
                    <tr>
                      <th className={`${thCl} w-14`}>S.No</th>
                      <th className={thCl}>Item Serial No</th>
                      {showPrice && <th className={thCl}>Price</th>}
                      <th className={`${thCl} w-20`}>Select</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {stnRows.map((row, idx) => (
                      <tr key={idx} className={`${stripCl(idx)} ${isDisabled ? "opacity-60 pointer-events-none" : ""} ${
                        row.IsSelected ? (isDark ? "bg-blue-900/20" : "bg-blue-50") : ""
                      }`}>
                        <td className={`${tdCl} text-center`}>{row.RowNumber ?? idx + 1}</td>
                        <td className={`${tdCl} font-mono`}>{row.ItemSno}</td>
                        {showPrice && (
                          <td className={tdCl}>
                            <input type="number" value={row.Price ?? ""}
                              onChange={e => handlePriceChange(idx, e.target.value)}
                              className={`w-24 px-2 py-1 rounded border text-sm ${inputCl}`}
                            />
                          </td>
                        )}
                        <td className={`${tdCl} text-center`}>
                          <input type="checkbox" checked={row.IsSelected}
                            onChange={() => toggleRow(idx)} disabled={isDisabled}
                            className="w-4 h-4 accent-blue-600 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex justify-center">
              <button onClick={handleCheckboxSave} disabled={saving || isDisabled}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg transition-all text-sm disabled:opacity-60">
                {saving ? <><Loader2 size={15} className="animate-spin" /> Saving...</> : <><Save size={15} /> Save &amp; Close</>}
              </button>
            </div>
          </div>

          {/* Right — side panels */}
          <div className="flex flex-col gap-5">

            {/* SI: selected SNos */}
            {type === "SI" && selectedRows.length > 0 && (
              <div className={`rounded-2xl border shadow ${card}`}>
                <p className={`px-5 pt-4 text-xs font-bold uppercase tracking-widest ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Selected Serial Nos
                </p>
                <div className="overflow-x-auto pb-4">
                  <table className="w-full mt-3">
                    <thead className="bg-emerald-600">
                      <tr>
                        <th className={thCl}>S.No</th>
                        <th className={thCl}>Serial No</th>
                        <th className={thCl}>Department</th>
                        <th className={thCl}>Vehicle</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {selectedRows.map((r, i) => (
                        <tr key={i} className={stripCl(i)}>
                          <td className={tdCl}>{r.RowNumber ?? i + 1}</td>
                          <td className={`${tdCl} font-mono`}>{r.ItemSno}</td>
                          <td className={tdCl}>{r.Dept}</td>
                          <td className={tdCl}>{r.Vehicle}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* SCRR: already received items */}
            {type === "SCRR" && (
              <div className={`rounded-2xl border shadow ${card}`}>
                <p className={`px-5 pt-4 pb-2 text-xs font-bold uppercase tracking-widest ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Already Received ({scrrReceived.length})
                </p>
                <div className="overflow-x-auto pb-4">
                  {scrrReceived.length === 0 ? (
                    <p className="text-center py-6 text-sm text-gray-400">Nil</p>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-red-500">
                        <tr>
                          <th className={thCl}>S.No</th>
                          <th className={thCl}>Serial No</th>
                          <th className={thCl}>Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {scrrReceived.map((r, i) => (
                          <tr key={i} className={stripCl(i)}>
                            <td className={tdCl}>{r.RowNumber ?? i + 1}</td>
                            <td className={`${tdCl} font-mono`}>{r.ItemSno}</td>
                            <td className={tdCl}>{r.IsClosed}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* Existing STN serial nos */}
            <div className={`rounded-2xl border shadow ${card}`}>
              <button onClick={() => setShowExisting(v => !v)}
                className="w-full flex items-center justify-between px-5 py-4 text-xs font-bold uppercase tracking-widest text-gray-500 hover:opacity-80 transition">
                <span>Existing Serial Nos ({existingSTN.length})</span>
                {showExisting ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showExisting && (
                <div className="pb-4 overflow-x-auto">
                  {existingSTN.length === 0 ? (
                    <p className="text-center py-6 text-sm text-gray-400">Nil</p>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-emerald-600">
                        <tr>
                          <th className={thCl}>S.No</th>
                          <th className={thCl}>Serial No</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {existingSTN.map((r, i) => (
                          <tr key={i} className={stripCl(i)}>
                            <td className={tdCl}>{r.RowNumber ?? i + 1}</td>
                            <td className={`${tdCl} font-mono`}>{r.ItemSno}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          PANEL C — Existing + Waiting (GRN/OB only)
          ══════════════════════════════════════════════════════════ */}
      {isInputPanel && (
        <>
          <div className={`rounded-2xl border shadow mb-5 ${card}`}>
            <button onClick={() => setShowExisting(v => !v)}
              className="w-full flex items-center justify-between px-5 py-4 text-xs font-bold uppercase tracking-widest text-gray-500 hover:opacity-80 transition">
              <span>Existing Serial Nos ({existingRows.length})</span>
              {showExisting ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showExisting && (
              <div className="pb-4 overflow-x-auto">
                {existingRows.length === 0 ? (
                  <p className="text-center py-6 text-sm text-gray-400">Nil</p>
                ) : (
                  <table className="w-full">
                    <thead className="bg-emerald-600">
                      <tr>
                        <th className={thCl}>S.No</th>
                        <th className={thCl}>Item Serial No</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {existingRows.map((r, i) => (
                        <tr key={i} className={stripCl(i)}>
                          <td className={`${tdCl} text-center`}>{r.RowNumber ?? i + 1}</td>
                          <td className={`${tdCl} font-mono`}>{r.ItemSno}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          <div className={`rounded-2xl border shadow mb-5 ${card}`}>
            <button onClick={() => setShowWaiting(v => !v)}
              className="w-full flex items-center justify-between px-5 py-4 text-xs font-bold uppercase tracking-widest text-gray-500 hover:opacity-80 transition">
              <span>Waiting for Confirmation ({waitingRows.length})</span>
              {showWaiting ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showWaiting && (
              <div className="pb-4 overflow-x-auto">
                {waitingRows.length === 0 ? (
                  <p className="text-center py-6 text-sm text-gray-400">Nil</p>
                ) : (
                  <table className="w-full">
                    <thead className="bg-amber-500">
                      <tr>
                        <th className={thCl}>S.No</th>
                        <th className={thCl}>Item Serial No</th>
                        <th className={thCl}>Mas ID</th>
                        <th className={thCl}>Row Index</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {waitingRows.map((r, i) => (
                        <tr key={i} className={stripCl(i)}>
                          <td className={`${tdCl} text-center`}>{r.RowNumber ?? i + 1}</td>
                          <td className={`${tdCl} font-mono`}>{r.ItemSno}</td>
                          <td className={tdCl}>{r.masid ?? r.MasID ?? "—"}</td>
                          <td className={tdCl}>{r.fld_RowIndex ?? r.RowIndex ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Unknown type fallback */}
      {!isInputPanel && !isCheckboxPanel && !loading && (
        <div className={`rounded-2xl border p-10 text-center shadow ${card}`}>
          <AlertCircle size={40} className="mx-auto mb-3 text-red-400" />
          <p className="font-bold text-red-500 text-lg">Unknown entry type: "{type || "(empty)"}"</p>
          <p className={`text-sm mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            URL must include <code className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">pro=SCRR</code> or{" "}
            <code className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">type=SCRR</code>
          </p>
          <p className="text-xs text-gray-400 mt-3 break-all font-mono">{window.location.search}</p>
        </div>
      )}
    </div>
  );
}